import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getdiscrepancyCollection } from "@tKgpco/Services/DiscrepancyService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import loadRMACollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMACollection";
import React, { use } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    rmaCollection: state.KGPState.RMAStore.RMACollection?.rmaOrders,
    rmaLoading: state.KGPState.RMAStore.isLoading,
    language: state.context.session.language,
    rmaParams: state.KGPState.RMAStore.RMACollectionParams,
    showPricing:
        state.context.showPricing && state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadRMACollection,
};

export interface RMAHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    tableContainer?: InjectableCss;
}

export const RMAHistoryTableStyles: RMAHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    tableContainer: {
        css: css`
            border-radius: 10px 0;
            width: 100%;
            overflow: auto;
        `,
    },
};

const styles = RMAHistoryTableStyles;

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    returnCode: ["returnCode"],
    poLineNumber: ["poLineNumber"],
    status: ["status"],
    poNumber: ["poNumber"],
    itemId: ["itemId"],
    partNumber: ["partNumber"],
    assignedTo: ["assignedTo"],
    createdOn: ["createdOn"],
};

const RMAHistoryTable: React.FC<Props> = ({
    rmaCollection,
    rmaLoading,
    language,
    loadRMACollection,
    rmaParams,
    showPricing,
}) => {
    if (rmaLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (rmaCollection?.length === 0 || !rmaCollection) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="rmaHistoryTable_noOrdersFound">
                    {translate("No RMA Orders found")}
                </Typography>
            </StyledWrapper>
        );
    }

    const changeSort = (sort: string) => {
        loadRMACollection({
            ...rmaParams,
            sort: rmaParams?.sort === sort ? `${sort} DESC` : sort,
        });
    };

    const headerClick = (sortField: string) => {
        const previousSort = rmaParams?.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        loadRMACollection({
            ...rmaParams,
            sort,
        });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!rmaParams?.sort) {
            return sorted;
        }
        const [primaryField] = rmaParams.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.tableContainer}>
                {rmaCollection?.length ? (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("returnCode")}
                                onSortClick={() => headerClick("returnCode")}
                                data-test-selector="tableHeader_RMANumber"
                            >
                                {translate("RMA #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("poLineNumber")}
                                onSortClick={() => headerClick("poLineNumber")}
                                data-test-selector="tableHeader_POLine"
                            >
                                {translate("PO Line #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("createdOn")}
                                onSortClick={() => headerClick("createdOn")}
                                data-test-selector="tableHeader_CreatedOn"
                            >
                                {translate("Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-date"
                                sorted={sorted("status")}
                                onSortClick={() => headerClick("status")}
                                data-test-selector="tableHeader_Status"
                            >
                                {translate("Status")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("poNumber")}
                                onSortClick={() => headerClick("poNumber")}
                                data-test-selector="tableHeader_PONumber"
                            >
                                {translate("PO #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("itemId")}
                                onSortClick={() => headerClick("itemId")}
                                data-test-selector="tableHeader_ItemId"
                            >
                                {translate("Supplier Part #")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("partNumber")}
                                onSortClick={() => headerClick("partNumber")}
                                data-test-selector="tableHeader_CustomerPartNumber"
                            >
                                {translate("Customer Part #")}
                            </DataTableHeader>

                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("assignedTo")}
                                onSortClick={() => headerClick("assignedTo")}
                                data-test-selector="tableHeader_AssignedTo"
                            >
                                {translate("Assigned To")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {rmaCollection?.map(rma => (
                                <DataTableRow key={rma.returnCode}>
                                    <DataTableCell className="col-shipto">
                                        {rma.returnCode ? (
                                            <Link
                                                href={`/MyAccount/RMAHistory/RMADetails?rmaNumber=${rma.returnCode}`}
                                                {...styles.orderNumberLink}
                                            >
                                                {rma.returnCode}
                                            </Link>
                                        ) : null}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{rma.poLineNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {rma.createdOn
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(rma.createdOn),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>

                                    <DataTableCell className="col-shipto">{rma.statusDisplay}</DataTableCell>
                                    <DataTableCell className="col-shipto">{rma.poNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">{rma.itemId}</DataTableCell>
                                    <DataTableCell className="col-shipto">{rma.partNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">{rma.assignedTo}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RMAHistoryTable)),
    definition: {
        group: "Common",
        displayName: "RMAHistoryTable",
    },
};

export default widgetModule;
